import emrService from '@/apis/emr/service';
import NoDataYet from '@/components/NoDataYet';
import BaseIconSvg from '@/components/bases/BaseIconSvg';
import BaseSpin from '@/components/bases/BaseSpin';
import useActions from '@/hooks/useActions';
import NoteBox from '@/screens/PHR/components/NoteBox';
import EmrInDay from '@/screens/Timeline/components/EmrInDay';
import FilterTimeline from '@/screens/Timeline/components/FilterTimeline';
import useTimelineStore from '@/screens/Timeline/store';
import useGlobalConfigs from '@/stores/globalConfigs';
import { usePhrStoreInContext } from '@/stores/phr';
import utils from '@/utils';
import { useQuickModal } from '@/utils/quickModal';
import { Button } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as React from 'react';

export interface ITimelineProps {}

export default function Timeline(props: ITimelineProps) {
  const [emrGroupByDate, setEmrGroupByDate] = React.useState<Record<string, IEMRData[]>>({});
  const setRefetchTimeline = useGlobalConfigs((s) => s.setRefetchTimeline);
  const uid = usePhrStoreInContext((s) => s.uid);
  const conditionTimeline = useTimelineStore((s) => s.conditionTimeline);
  const { quickModal, quickModalContext } = useQuickModal();
  const setModal = useTimelineStore((s) => s.setModal);
  const { isCMSView } = useActions();
  const conditionGetGroup = {
    uid,
    type: conditionTimeline.types.join(',').trim(),
    tag: conditionTimeline.tags.join(',').trim(),
  };

  const { data, refetch, fetchNextPage, isFetching, isFetchingNextPage } = emrService.useGetGroupInfinite(
    conditionGetGroup,
    (res) => setEmrGroupByDate((e) => ({ ...e, ...(res?.data || {}) })),
  );

  React.useEffect(() => {
    setEmrGroupByDate((e) => data?.pages?.reduce((d: any, res: any) => ({ ...d, ...res.data }), e));
  }, [data]);

  React.useEffect(() => {
    setRefetchTimeline(refetch);
  }, [refetch, setRefetchTimeline]);
  // Xử lý kéo scroll
  const divRef = React.useRef<any>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startX, setStartX] = React.useState<number>(0);
  const [startY, setStartY] = React.useState<number>(0);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [draggingOccurred, setDraggingOccurred] = React.useState(false);

  const handleMouseDown = (e: any) => {
    setIsDragging(true);
    setStartX(e.pageX - divRef.current.offsetLeft);
    setStartY(e.pageY - divRef.current.offsetTop);
    setScrollLeft(divRef.current.scrollLeft);
    setScrollTop(divRef.current.scrollTop);
    setDraggingOccurred(false);
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging) return;
    const x = e.pageX - divRef.current.offsetLeft;
    const walkX = (startX - x) * 2;
    divRef.current.scrollLeft = scrollLeft - walkX;

    const y = e.pageY - divRef.current.offsetTop;
    const walkY = (y - startY) * 2;
    divRef.current.scrollTop = scrollTop - walkY;
    setDraggingOccurred(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleClick = (e: any) => {
    if (draggingOccurred) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleScroll = React.useCallback(() => {
    if (
      !isFetchingNextPage &&
      divRef.current?.scrollLeft >= divRef.current?.scrollWidth - divRef.current?.clientWidth - 300
    ) {
      // Chỗ này sau này xử lý prefetch
    }

    // Kiểm tra khi cuộn đến cuối
    if (
      !isFetchingNextPage &&
      divRef.current?.scrollLeft >= divRef.current?.scrollWidth - divRef.current?.clientWidth - 100
    ) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage]);

  const sortKey = Object.keys(emrGroupByDate || {}).sort(
    (a, b) => dayjs(b, 'YYYY-MM-DD').unix() - dayjs(a, 'YYYY-MM-DD').unix(),
  );

  React.useEffect(() => {
    // Lấy dữ liệu khi chưa trigger được sự kiện scroll
    if (divRef.current?.scrollWidth <= divRef.current?.clientWidth) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetching]);

  React.useEffect(() => setModal(quickModal), [quickModal, setModal]);

  return (
    <>
      {quickModalContext}
      <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-1 rounded-2xl shadow flex flex-col">
        <>
          <div className="rounded-t-lg p-2 border-b border-gray-3 flex justify-between items-center">
            <div className="uppercase font-bold">Dòng thời gian</div>
            <div className="flex gap-2">
              <FilterTimeline />

              {!isCMSView && (
                <Button
                  className="p-2 border-none"
                  onClick={() =>
                    quickModal.custom({
                      title: 'Ghi chú',
                      content: <NoteBox />,
                    })
                  }
                >
                  <BaseIconSvg src={'/icons/Note.svg'} />
                </Button>
              )}
            </div>
          </div>

          <div
            className={`container-timeline -scale-x-100  custom-scroll w-full h-full rounded-b-lg overflow-auto relative ${isDragging ? 'cursor-grabbing select-none' : ''}`}
            ref={divRef}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onScroll={handleScroll}
          >
            {_.isEmpty(emrGroupByDate) ? (
              <div className="-scale-x-100">
                <NoDataYet />
              </div>
            ) : (
              <div className="w-fit relative h-full -scale-x-100">
                <div className="flex relative z-10">
                  {sortKey?.reverse()?.map((k, i) => {
                    const emrInDays = emrGroupByDate?.[k];

                    const range = utils.rangeDateToString(
                      dayjs(k, 'YYYY-MM-DD').toDate(),
                      dayjs(sortKey[i + 1], 'YYYY-MM-DD').toDate(),
                    );

                    return <EmrInDay key={k} emrs={emrInDays} range={range} />;
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="absolute bottom-4 overflow-y-auto left-0 z-20 flex w-full minimize-bar " />
        </>
      </div>
      {isFetchingNextPage && (
        <div className="absolute z-20 top-0 left-0 bottom-0 right-0 bg-gray-1 bg-opacity-80 flex justify-center items-center">
          <BaseSpin />
        </div>
      )}
    </>
  );
}
